import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';
import { buildMediaUserUrl, instagramConfig } from '../../lib/SocialMedias';
import InstagramNotConfigured from '../../modules/instagram-config/InstagramNotConfigured';
import { InstagramConfig } from '../../modules/instagram-config/instagram.repository';
import useInstagramConfig, { OwnerType } from '../../modules/instagram-config/useInstagramConfig';
import useTeceoStyles from '../../useTeceoStyles';
import InstagramFeed from './InstagramFeed';
import InstagramFeedSectionLoading from './InstagramFeedSectionLoading';
import Section, { SectionProps } from './Section';

const stringValidator = yup.string().trim().required();

type InstagramFeedSectionProps = {
  sectionProps: Partial<SectionProps>;
  ownerId: string;
  ownerType: OwnerType;
  username?: string;
};

type EmptyToken = { tag: 'empty' };
type InvalidToken = { tag: 'invalid' };
type ValidToken = {
  tag: 'valid';
} & InstagramConfig;

type InstagramConfigStatus = EmptyToken | InvalidToken | ValidToken;

const InstagramFeedSection = ({
  sectionProps,
  username = 'instagram',
  ownerType,
  ownerId,
}: InstagramFeedSectionProps) => {
  const { loadConfig: loadInstagramConfig, initInstagramConfig } = useInstagramConfig();
  const [state, setState] = useState<InstagramConfigStatus>({ tag: 'empty' });
  const { classes: teceoClasses } = useTeceoStyles();

  const loadConfig = useCallback(
    async (tp: OwnerType, id: string) => {
      const configResponse = await loadInstagramConfig(tp, id);
      if (configResponse.ok && configResponse.data) {
        setState({ ...configResponse.data, tag: 'valid' });
      } else {
        setState({ tag: 'invalid' });
      }
    },
    [loadInstagramConfig],
  );

  useEffect(() => {
    if (!stringValidator.isValidSync(ownerId) || !stringValidator.isValidSync(ownerType)) {
      return;
    }
    loadConfig(ownerType, ownerId);
  }, [ownerType, ownerId, loadConfig]);

  if (state.tag === 'empty') {
    return <InstagramFeedSectionLoading {...sectionProps} />;
  }

  if (state.tag === 'invalid') {
    return sectionProps.readOnly ? null : (
      <Section {...sectionProps}>
        <InstagramNotConfigured justify="center" onConfigure={initInstagramConfig} />
      </Section>
    );
  }

  return (
    <Section {...sectionProps}>
      <Box
        sx={{
          pb: 2,
        }}
      >
        {username ? (
          <a
            href={buildMediaUserUrl(instagramConfig, username)}
            className={teceoClasses.linkDisguised}
            target="_blank"
            rel="noreferrer"
          >
            <Typography variant="h5" color="text.secondary" align="center">
              @{username}
            </Typography>
          </a>
        ) : (
          <Typography variant="h5" color="text.secondary" align="center">
            instagram
          </Typography>
        )}
      </Box>
      <InstagramFeed instagramToken={state.token} />
    </Section>
  );
};

export default InstagramFeedSection;
